// src/data/items/potions.js
// Vials and potions for Alchemy skill

// Vials (containers for potions)
export const VIALS = [
    { id: 'vial_of_water', name: 'Vial of Water', color: '#4169E1', category: 'vial', type: 'resource', value: 1 },
];

// Potions (crafted from herbs) - each has combat effects
// Category determines which potions conflict (can't have two of same category active)
// Duration is in milliseconds, only counts down while in combat
export const POTIONS = [
    // Early potions (levels 10-34)
    {
        id: 'feral_draft',
        name: 'Feral Draft',
        color: '#DC143C',
        category: 'potion',
        type: 'consumable',
        value: 35,
        effects: {
            category: 'attack',
            duration: 180000, // 3 minutes
            attackBonus: 8
        }
    },
    {
        id: 'steady_brew',
        name: 'Steady Brew',
        color: '#4169E1',
        category: 'potion',
        type: 'consumable',
        value: 55,
        effects: {
            category: 'accuracy',
            duration: 180000,
            accuracyBonus: 10 // +10% accuracy
        }
    },
    {
        id: 'guard_tonic',
        name: 'Guard Tonic',
        color: '#4682B4',
        category: 'potion',
        type: 'consumable',
        value: 75,
        effects: {
            category: 'defense',
            duration: 180000,
            defenseBonus: 8
        }
    },
    {
        id: 'quickening_draught',
        name: 'Quickening Draught',
        color: '#FFD700',
        category: 'potion',
        type: 'consumable',
        value: 100,
        effects: {
            category: 'speed',
            duration: 180000,
            speedBonus: 15 // -15% attack interval
        }
    },

    // Mid potions (levels 42-62)
    {
        id: 'balanced_infusion',
        name: 'Balanced Infusion',
        color: '#9370DB',
        category: 'potion',
        type: 'consumable',
        value: 120,
        effects: {
            category: 'hybrid',
            duration: 240000, // 4 minutes
            attackBonus: 5,
            defenseBonus: 5
        }
    },
    {
        id: 'focused_elixir',
        name: 'Focused Elixir',
        color: '#FF4500',
        category: 'potion',
        type: 'consumable',
        value: 155,
        effects: {
            category: 'crit',
            duration: 180000,
            critBonus: 10 // +10% crit chance
        }
    },
    {
        id: 'sustaining_brew',
        name: 'Sustaining Brew',
        color: '#228B22',
        category: 'potion',
        type: 'consumable',
        value: 165,
        effects: {
            category: 'reduction',
            duration: 240000,
            damageReduction: 10 // -10% damage taken
        }
    },
    {
        id: 'hardened_infusion',
        name: 'Hardened Infusion',
        color: '#708090',
        category: 'potion',
        type: 'consumable',
        value: 200,
        effects: {
            category: 'defense',
            duration: 240000,
            defenseBonus: 15
        }
    },

    // High potions (levels 68-80)
    {
        id: 'rallying_elixir',
        name: 'Rallying Elixir',
        color: '#FF1493',
        category: 'potion',
        type: 'consumable',
        value: 250,
        effects: {
            category: 'melee',
            duration: 180000,
            attackBonus: 15,
            meleeDamageBonus: 20, // +20% melee damage
            decays: true, // Attack bonus decays from 15 to 5 over duration
            minBonus: 5
        }
    },
    {
        id: 'veiled_draught',
        name: 'Veiled Draught',
        color: '#483D8B',
        category: 'potion',
        type: 'consumable',
        value: 300,
        effects: {
            category: 'evasion',
            duration: 240000,
            enemyAccuracyReduction: 15 // -15% enemy accuracy
        }
    },
    {
        id: 'arcane_tincture',
        name: 'Arcane Tincture',
        color: '#9400D3',
        category: 'potion',
        type: 'consumable',
        value: 400,
        effects: {
            category: 'magic',
            duration: 300000, // 5 minutes
            magicDamageBonus: 20, // +20% magic damage
            glyphSaveChance: 15 // 15% chance to not consume glyphs
        }
    },
    {
        id: 'sharpshot_brew',
        name: 'Sharpshot Brew',
        color: '#006400',
        category: 'potion',
        type: 'consumable',
        value: 450,
        effects: {
            category: 'ranged',
            duration: 300000,
            rangedDamageBonus: 20, // +20% ranged damage
            ammoSaveChance: 15 // 15% chance to not consume ammo
        }
    },

    // Ultimate potion (level 90) - THE endgame potion
    // Replaces: Feral/Rallying (attack), Guard/Hardened (defense), Steady (accuracy),
    //           Quickening (speed), Focused (crit), and all 3 damage type potions
    // Only Veiled Draught (evasion) and Sustaining Brew (reduction) remain useful alternatives
    {
        id: 'ascendant_concoction',
        name: 'Ascendant Concoction',
        color: '#FFD700',
        category: 'potion',
        type: 'consumable',
        value: 800,
        effects: {
            category: 'all', // Conflicts with all other potions
            duration: 300000, // 5 minutes
            attackBonus: 15,        // Same as Rallying peak
            defenseBonus: 15,       // Same as Hardened
            accuracyBonus: 15,      // Better than Steady (10%)
            speedBonus: 15,         // Same as Quickening (-15% attack interval)
            critBonus: 10,          // Same as Focused
            meleeDamageBonus: 25,   // HIGHER than Rallying (20%) - replaces it
            rangedDamageBonus: 25,  // HIGHER than Sharpshot (20%) - replaces it
            magicDamageBonus: 25,   // HIGHER than Arcane (20%) - replaces it
            corruptionBonus: 5      // +5% corruption proc (unique bonus)
        }
    },
];

// HD icons — filename matches item id (assets/icons/alchemy/{id}.png)
for (const item of [...VIALS, ...POTIONS]) {
    item.sprite = { sheet: `assets/icons/alchemy/${item.id}.png`, x: 0, y: 0, width: 64, height: 64 };
}
